import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback } from 'react';
import { getWebSocketManager, resetWebSocketManager } from '../utility/websocket';
// Context for WebSocket state
const WebSocketContext = React.createContext(null);
// Provider component
export const WebSocketProvider = ({ children, token }) => {
    const [state, setState] = React.useState({
        isConnected: false,
        connectionState: 'closed',
    });
    const wsManager = React.useRef(null);
    const subscriptionsRef = React.useRef(new Map());
    // Initialize WebSocket manager
    React.useEffect(() => {
        if (!wsManager.current) {
            wsManager.current = getWebSocketManager();
        }
        // Listen to connection changes
        const unsubscribe = wsManager.current.onConnectionChange((connected) => {
            setState({
                isConnected: connected,
                connectionState: wsManager.current.getState(),
            });
        });
        return () => {
            unsubscribe();
        };
    }, []);
    // Connect/disconnect based on token
    React.useEffect(() => {
        if (!wsManager.current)
            return;
        if (token) {
            // Connect with token
            wsManager.current.connect(token);
        }
        else {
            // Disconnect if no token
            wsManager.current.disconnect();
        }
        // Cleanup on unmount
        return () => {
            if (wsManager.current) {
                wsManager.current.disconnect();
            }
        };
    }, [token]);
    // Send message function
    const send = useCallback((type, data) => {
        var _a;
        (_a = wsManager.current) === null || _a === void 0 ? void 0 : _a.send(type, data);
    }, []);
    // Subscribe to message type
    const subscribe = React.useCallback((messageType, handler) => {
        if (!wsManager.current)
            return;
        // Track subscription locally
        if (!subscriptionsRef.current.has(messageType)) {
            subscriptionsRef.current.set(messageType, new Set());
        }
        subscriptionsRef.current.get(messageType).add(handler);
        // Subscribe to WebSocket manager
        wsManager.current.on(messageType, handler);
    }, []);
    // Unsubscribe from message type
    const unsubscribe = React.useCallback((messageType, handler) => {
        const handlers = subscriptionsRef.current.get(messageType);
        if (handlers) {
            handlers.delete(handler);
            if (handlers.size === 0) {
                subscriptionsRef.current.delete(messageType);
            }
        }
    }, []);
    const value = {
        isConnected: state.isConnected,
        connectionState: state.connectionState,
        send,
        subscribe,
        unsubscribe,
    };
    return _jsx(WebSocketContext.Provider, { value: value, children: children });
};
// Hook to use WebSocket in components
export const useWebSocket = () => {
    const context = React.useContext(WebSocketContext);
    if (!context) {
        throw new Error('useWebSocket must be used within WebSocketProvider');
    }
    return context;
};
// Hook to subscribe to specific message types
export const useWebSocketMessage = (messageType, handler, deps = []) => {
    const ws = useWebSocket();
    React.useEffect(() => {
        const messageHandler = (message) => {
            handler(message.data);
        };
        ws.subscribe(messageType, messageHandler);
        return () => {
            ws.unsubscribe(messageType, messageHandler);
        };
    }, [messageType, ws, ...deps]);
};
// Hook to get connection status
export const useWebSocketStatus = () => {
    const ws = useWebSocket();
    return {
        isConnected: ws.isConnected,
        status: ws.connectionState,
    };
};
// Reset WebSocket manager (useful for testing or logout)
export const useWebSocketReset = () => {
    return React.useCallback(() => {
        resetWebSocketManager();
    }, []);
};
